// Pure-string parsers for the model's plan/step formalism.
//
// The model is prompted (see kyoto/copilot/services/prompts.py) to emit:
//   <plan>
//   Goal: <one line>
//   Completion criteria: <conditions>
//   Steps:
//     1. <subgoal>
//     2. <subgoal>
//   </plan>
//
// And on every subsequent response:
//   Current step: 2/5 — opening the file manager
//
// Kyoto persists these as `state.current_plan` (the body inside <plan>...</plan>)
// and `state.current_step` (the value after "Current step:"). Denver receives the
// plan in the assistant_text of every turn and parses it locally so the chat
// panel can render a live progress strip without an extra API roundtrip.

const PLAN_RE = /<plan>([\s\S]*?)<\/plan>/g;
// Match `Current step: N/M — …` anywhere in the text, not just at line start.
// The model often prefixes the marker with a transition word
// ("Excellent! Current step: 3/4 — …") and an anchored regex silently
// dropped those, freezing the progress strip on the last clean line.
// Anchoring on the N/M digit-pair right after the colon avoids picking up
// narrative prose like "Let me show you the current step:".
const CURRENT_STEP_RE = /Current step:\s*(\d+\s*\/\s*\d+[^\n]*?)\s*(?:\n|$)/gi;
const STEPS_HEADER_RE = /^\s*Steps:\s*$/im;
const NUMBERED_STEP_RE = /^\s*(\d+)\.\s+(.*\S)\s*$/;
const STEP_INDEX_RE = /^(?:Current step:\s*)?(\d+)\s*\/\s*(\d+)\s*(?:[—\-:]\s*(.*\S))?\s*$/;

/** Return the inner body of the LATEST `<plan>...</plan>` block. */
export function extractPlanBody(text) {
  if (!text) return "";
  let last = "";
  let m;
  PLAN_RE.lastIndex = 0;
  while ((m = PLAN_RE.exec(text)) !== null) last = m[1];
  return last.trim();
}

/**
 * Return the latest "Current step:" value in the text, or "".
 * Latest-wins matches kyoto's extract_current_step (state_capsule.py) so an
 * assistant turn that announces progress more than once lands on the most
 * advanced step rather than the first.
 */
export function extractCurrentStep(text) {
  if (!text) return "";
  CURRENT_STEP_RE.lastIndex = 0;
  let last = null;
  let m;
  while ((m = CURRENT_STEP_RE.exec(text)) !== null) last = m;
  return last ? last[1].trim() : "";
}

/**
 * Parse the plan body into a list of {index, text} steps.
 *
 * Prefers numbered lines under a "Steps:" header. Falls back to any numbered
 * lines in the body when the header is absent — the model occasionally drops
 * the header on short tasks.
 */
export function parsePlanSteps(body) {
  if (!body) return [];
  const lines = body.split("\n");
  let scanFrom = 0;
  for (let i = 0; i < lines.length; i++) {
    if (STEPS_HEADER_RE.test(lines[i])) {
      scanFrom = i + 1;
      break;
    }
  }
  const out = [];
  for (let i = scanFrom; i < lines.length; i++) {
    const m = NUMBERED_STEP_RE.exec(lines[i]);
    if (m) out.push({ index: Number(m[1]), text: m[2].trim() });
  }
  return out;
}

/**
 * Parse a "Current step:" value into {current, total, label}.
 * Returns null when the value doesn't match the "N/M [— label]" shape.
 */
export function parseCurrentStep(value) {
  if (!value) return null;
  const m = STEP_INDEX_RE.exec(value);
  if (!m) return null;
  return {
    current: Number(m[1]),
    total: Number(m[2]),
    label: (m[3] || "").trim(),
  };
}
